import { useSeoMeta } from '@unhead/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFundraisers } from '@/hooks/useFundraisers';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollowing } from '@/hooks/useFollowing';
import { Header } from '@/components/Header';
import { FundraiserCard } from '@/components/FundraiserCard';
import FundraiserDetail from '@/pages/FundraiserDetail';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, Tag, Users } from 'lucide-react';

const Index = () => {
  const { data: allFundraisers, isLoading, refetch } = useFundraisers();
  const { user } = useCurrentUser();
  const { data: following } = useFollowing(user?.pubkey);
  const [searchParams] = useSearchParams();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showFollowingOnly, setShowFollowingOnly] = useState(false);
  const [zapAnimation, setZapAnimation] = useState(false);

  useSeoMeta({
    title: 'ZapFunder - Decentralized Crowdfunding on Nostr',
    description: 'Fund innovative projects with Bitcoin Lightning. Transparent, censorship-resistant crowdfunding.',
  });

  // Check if we're viewing a specific fundraiser via ?fund=eventId
  const fundEventId = searchParams.get('fund');
  const viewingFundraiser = fundEventId && allFundraisers?.find(f => f.event.id === fundEventId);
  
  // Show loading popup when we have a fund param but fundraisers haven't loaded yet
  const showLoadingPopup = fundEventId && isLoading;

  // Filter fundraisers from last 2 weeks
  const recentFundraisers = useMemo(() => {
    if (!allFundraisers) return [];
    
    const twoWeeksAgo = Math.floor(Date.now() / 1000) - (14 * 24 * 60 * 60);
    
    return allFundraisers.filter(f => f.createdAt >= twoWeeksAgo);
  }, [allFundraisers]);

  // Get all unique hashtags from fundraisers
  const allHashtags = useMemo(() => {
    const tags = new Set<string>();
    recentFundraisers.forEach(f => {
      f.hashtags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [recentFundraisers]);

  // Filter by selected tag, following, and sort by newest
  const fundraisers = useMemo(() => {
    let filtered = recentFundraisers;

    // Filter by tag
    if (selectedTag) {
      filtered = filtered.filter(f => f.hashtags.includes(selectedTag));
    }

    // Filter by following
    if (showFollowingOnly && following && following.length > 0) {
      filtered = filtered.filter(f => following.includes(f.pubkey));
    }
    
    // Sort by newest first
    return [...filtered].sort((a, b) => b.createdAt - a.createdAt);
  }, [recentFundraisers, selectedTag, showFollowingOnly, following]);

  const handleZapSuccess = () => {
    setZapAnimation(true);
    setTimeout(() => setZapAnimation(false), 2000);
    refetch();
  };

  // If viewing a specific fundraiser, render detail view
  if (viewingFundraiser) {
    return <FundraiserDetail fundraiser={viewingFundraiser} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <Header />

      {/* Loading Fundraiser Popup */}
      <AnimatePresence>
        {showLoadingPopup && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="bg-white border-2 border-orange-500 rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="h-5 w-5 text-orange-500" />
              </motion.div>
              <p className="text-base font-bold text-black">Loading fundraiser...</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-12"
        >
          <div className="flex items-center justify-center mb-6">
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 10, -10, 0]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            >
              <Sparkles className="h-16 w-16 text-orange-500 drop-shadow-lg" />
            </motion.div>
          </div>
          
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black text-black mb-4 sm:mb-6 tracking-tight text-center drop-shadow-sm px-4">
            Active Campaigns
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-semibold text-center mb-8 sm:mb-10 px-4">
            Support groundbreaking projects building the future on Nostr and Bitcoin Lightning
          </p>

          {/* Filter Controls */}
          <div className="flex flex-col items-center gap-4 sm:gap-6">
            {/* Following Filter */}
            {user && following && following.length > 0 && (
              <div className="flex items-center justify-center">
                <Button
                  onClick={() => setShowFollowingOnly(!showFollowingOnly)}
                  variant={showFollowingOnly ? 'default' : 'outline'}
                  className={showFollowingOnly
                    ? 'bg-orange-500 text-white font-bold border-2 border-orange-600 shadow-lg h-10 sm:h-11'
                    : 'border-2 border-orange-500/30 text-orange-600 hover:bg-orange-50 font-bold h-10 sm:h-11'
                  }
                >
                  <Users className="h-4 w-4 mr-2" />
                  Following Only
                </Button>
              </div>
            )}

            {/* Hashtag Filter */}
            {allHashtags.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 px-4">
                <Tag className="h-4 w-4 text-orange-600 hidden sm:block" />
                <Badge
                  onClick={() => setSelectedTag(null)}
                  className={`cursor-pointer px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold transition-all ${
                    !selectedTag
                      ? 'bg-orange-500 text-white border-2 border-orange-600'
                      : 'bg-white text-orange-600 border-2 border-orange-500/30 hover:bg-orange-50'
                  }`}
                >
                  All
                </Badge>
                {allHashtags.map(tag => (
                  <Badge
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`cursor-pointer px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold transition-all ${
                      selectedTag === tag
                        ? 'bg-orange-500 text-white border-2 border-orange-600'
                        : 'bg-white text-orange-600 border-2 border-orange-500/30 hover:bg-orange-50'
                    }`}
                  >
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="border-2 border-orange-500/20 bg-white">
                <Skeleton className="h-56 sm:h-64 w-full" />
                <CardContent className="p-4 sm:p-8 space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 sm:h-12 w-10 sm:w-12 rounded-full" />
                    <Skeleton className="h-4 sm:h-5 w-24 sm:w-32" />
                  </div>
                  <Skeleton className="h-6 sm:h-8 w-3/4" />
                  <Skeleton className="h-4 sm:h-5 w-full" />
                  <Skeleton className="h-3 sm:h-4 w-full" />
                  <div className="flex justify-between pt-4">
                    <Skeleton className="h-5 sm:h-6 w-20 sm:w-28" />
                    <Skeleton className="h-5 sm:h-6 w-16 sm:w-20" />
                  </div>
                  <Skeleton className="h-12 sm:h-14 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Fundraisers Grid */}
        {!isLoading && fundraisers.length > 0 && (
          <div className="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {fundraisers.map((fundraiser, index) => (
              <FundraiserCard 
                key={fundraiser.id} 
                fundraiser={fundraiser} 
                index={index}
                onZapSuccess={handleZapSuccess}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && fundraisers.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-orange-500/20 bg-white shadow-2xl">
              <CardContent className="py-24 px-8 text-center">
                <motion.div
                  animate={{ 
                    y: [0, -15, 0],
                    rotate: [0, 15, -15, 0]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className="mb-8"
                >
                  <Sparkles className="h-24 w-24 text-orange-500 mx-auto drop-shadow-lg" />
                </motion.div>
                <h3 className="text-3xl sm:text-4xl font-black text-black mb-4 px-4">
                  {showFollowingOnly 
                    ? 'No Campaigns from Following'
                    : selectedTag 
                      ? `No Campaigns in #${selectedTag}` 
                      : 'No Recent Campaigns'}
                </h3>
                <p className="text-lg sm:text-xl text-gray-700 max-w-md mx-auto font-semibold mb-6 px-4">
                  {showFollowingOnly
                    ? 'People you follow haven\'t created campaigns recently. Try viewing all campaigns.'
                    : selectedTag 
                      ? 'Try selecting a different category or check back later.'
                      : 'Check back soon for new campaigns, or be the first to launch one this week.'}
                </p>
                {(selectedTag || showFollowingOnly) && (
                  <div className="flex gap-3">
                    {selectedTag && (
                      <Button
                        onClick={() => setSelectedTag(null)}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                      >
                        Clear Tag Filter
                      </Button>
                    )}
                    {showFollowingOnly && (
                      <Button
                        onClick={() => setShowFollowingOnly(false)}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
                      >
                        View All Campaigns
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-orange-500/20 mt-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center space-y-4">
            <p className="text-lg font-semibold text-gray-700">
              Powered by{' '}
              <a
                href="https://nostr.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 hover:text-orange-700 transition-colors font-bold"
              >
                Nostr
              </a>
              {' '}and{' '}
              <a
                href="https://bitcoin.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 hover:text-orange-700 transition-colors font-bold"
              >
                Bitcoin Lightning
              </a>
            </p>
            <p className="text-base font-bold text-gray-700">
              Built with ❤️ by{' '}
              <a
                href="https://uno.dog"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 hover:text-orange-700 transition-colors"
              >
                Uno
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
